import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import UploadDialog from "@/components/modals/UploadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { gbp } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import { Plus, UploadCloud, Search, Pencil, Trash2, Download } from "lucide-react";

const COMPONENTS = ["picking", "storage", "movement", "delivery", "activity", "other"];
const BLANK = { product_code: "", description: "", category: "", site: "", basis: "Full Pallet", picking: 0, storage: 0, movement: 0, delivery: 0, activity: 0, other: 0 };
const dapTotal = (r) => COMPONENTS.reduce((a, c) => a + (Number(r[c]) || 0), 0);

export default function DapPricingTable() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["dap-pricing"], queryFn: () => api.get("/dap-pricing").then((r) => r.data) });

  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const cats = useMemo(() => [...new Set(data.map((d) => d.category).filter(Boolean))].sort(), [data]);
  const filtered = useMemo(() => data.filter((r) => {
    if (cat !== "all" && r.category !== cat) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!`${r.product_code} ${r.description} ${r.category}`.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [data, cat, search]);

  const openAdd = () => { setEditing(null); setForm(BLANK); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ product_code: r.product_code, description: r.description, category: r.category, site: r.site || "", basis: r.basis || "Full Pallet", picking: r.picking, storage: r.storage, movement: r.movement, delivery: r.delivery, activity: r.activity, other: r.other }); setDialogOpen(true); };

  const autofill = async () => {
    setAutofilling(true);
    try {
      const { data } = await api.get("/dap-defaults", { params: { site: form.site, basis: form.basis } });
      setForm((f) => ({ ...f, picking: data.picking, storage: data.storage, movement: data.movement, delivery: data.delivery, activity: data.activity }));
      toast.success(`Filled from ${form.basis} rates`);
    } catch (err) { toast.error("Could not load rates"); }
    finally { setAutofilling(false); }
  };

  const save = async () => {
    if (!form.product_code.trim()) { toast.error("Product code is required"); return; }
    setSaving(true);
    try {
      const p = { ...form };
      COMPONENTS.forEach((k) => (p[k] = Number(p[k]) || 0));
      if (editing) await api.put(`/dap-pricing/${editing.id}`, p);
      else await api.post("/dap-pricing", p);
      toast.success(editing ? "DAP breakdown updated" : "DAP breakdown added");
      qc.invalidateQueries({ queryKey: ["dap-pricing"] });
      setDialogOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/dap-pricing/${deleteTarget.id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["dap-pricing"] }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  const exportCsv = () => downloadCsv("epgroup-dap-pricing.csv", ["product_code", "description", "category", ...COMPONENTS, "total_dap"], filtered.map((r) => ({ ...r, total_dap: dapTotal(r).toFixed(2) })));

  return (
    <div>
      <PageHeader title="DAP Pricing by SKU" subtitle="Delivered-at-place price broken down per SKU: picking, storage, movement, delivery, activity and other.">
        <Button variant="outline" onClick={exportCsv} data-testid="dap-export-button"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
        <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="dap-upload-button"><UploadCloud className="w-4 h-4 mr-1.5" /> Upload</Button>
        <Button onClick={openAdd} className="bg-[#78BE20] hover:bg-[#5fa016]" data-testid="dap-add-button"><Plus className="w-4 h-4 mr-1.5" /> Add SKU</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search SKU, description or category…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="dap-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-full sm:w-52" data-testid="dap-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : (
        <TableShell>
          <table className="w-full text-sm" data-testid="dap-pricing-table">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Basis</th>
                <th className="px-4 py-3 text-right">Picking</th>
                <th className="px-4 py-3 text-right">Storage</th>
                <th className="px-4 py-3 text-right">Movement</th>
                <th className="px-4 py-3 text-right">Delivery</th>
                <th className="px-4 py-3 text-right">Activity</th>
                <th className="px-4 py-3 text-right">Other</th>
                <th className="px-4 py-3 text-right">Total DAP</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`dap-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono-num font-medium text-slate-800 dark:text-slate-200">{r.product_code}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[220px] truncate">{r.description || "—"}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">{r.category || "—"}</span></td>
                  <td className="px-4 py-3 text-slate-600">{r.site || "—"}</td>
                  <td className="px-4 py-3">{r.basis ? <span className={`text-xs rounded-full px-2 py-0.5 ${r.basis === "Case" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`}>{r.basis}</span> : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.picking)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.storage)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.movement)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.delivery)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.activity)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.other)}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-blue-700">{gbp(dapTotal(r))}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`dap-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(r)} data-testid={`dap-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState message="No DAP pricing rows match your filters." />}
        </TableShell>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="dap-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit DAP breakdown" : "Add DAP breakdown"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Product code (SKU) *</Label><Input className="mt-1.5" value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} data-testid="dap-form-code" /></div>
            <div><Label>Category</Label><Input className="mt-1.5" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Site (for delivery rate)</Label><Input className="mt-1.5" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="IPL Normanton" data-testid="dap-form-site" /></div>
            <div><Label>Rate basis</Label>
              <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
                <SelectTrigger className="mt-1.5" data-testid="dap-form-basis"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Full Pallet">Full Pallet</SelectItem><SelectItem value="Case">Case</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Input className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="col-span-2">
              <Button type="button" variant="outline" size="sm" onClick={autofill} disabled={autofilling} data-testid="dap-autofill-button">{autofilling ? "Filling…" : `Auto-fill from ${form.basis} rates`}</Button>
            </div>
            {COMPONENTS.map((c) => (
              <div key={c}><Label className="capitalize">{c} (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form[c]} onChange={(e) => setForm({ ...form, [c]: e.target.value })} /></div>
            ))}
            <div className="col-span-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 flex items-center justify-between">
              <span className="text-sm font-medium text-blue-800">Total DAP price</span>
              <span className="font-mono-num font-bold text-blue-800">{gbp(dapTotal(form))}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#78BE20] hover:bg-[#5fa016]" data-testid="dap-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        entity="dap-pricing"
        columns={["product_code", "description", "category", "site", "basis", ...COMPONENTS]}
        onDone={() => qc.invalidateQueries({ queryKey: ["dap-pricing"] })}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete DAP breakdown?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove <span className="font-medium">{deleteTarget?.product_code}</span>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
